import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { Capacitor } from '@capacitor/core'
import { api } from '../../lib/api'
import QRCode from 'qrcode'
import { downloadBlob, downloadDataUrl } from '../../lib/download'
import { TariffDot } from './TariffsPage'
import { useModule } from '../../lib/modules'
import { useSearchParams } from 'react-router-dom'

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  free: { label: 'Свободен', color: 'bg-green-100 text-green-700' },
  reserved: { label: 'Забронирован', color: 'bg-yellow-100 text-yellow-700' },
  in_use: { label: 'В аренде', color: 'bg-blue-100 text-blue-700' },
  service: { label: 'Обслуживание', color: 'bg-orange-100 text-orange-700' },
}

interface Tariff { id: string; name: string; hourlyRate: number; dailyRate?: number; color?: string | null }
interface Bike {
  id: string; code: string; type?: string | null; model?: string; status: string; qrData: string; notes?: string
  category?: string | null
  point?: { id: string; name: string }
  tariff?: Tariff | null
  tariffId?: string | null
}
interface Point { id: string; name: string }

type CreateForm = { code: string; tariffId: string; model: string; pointId: string }
type EditForm = { tariffId: string; model: string; pointId: string; notes: string; category: string }

/** #11 suggested inventory categories (free text — the owner may type anything). */
const CATEGORY_SUGGESTIONS = ['Велосипед', 'Замок', 'Аксессуар', 'Шлем', 'Детское кресло']

interface RepairHistoryItem {
  id: string; status: string; reason: string; mechanicName: string; openedAt: string; closedAt: string | null
  totalCost: number | null; works: { type: string; hours: number | null }[]; parts: { name: string; quantity: number }[]
}

/** #7 Owner-facing repair history for a bike — who fixed it, what work, what parts. */
function RepairHistory({ bikeId }: { bikeId: string }) {
  const { data, isLoading } = useQuery<{ history: RepairHistoryItem[] }>({
    queryKey: ['bike-repairs', bikeId],
    queryFn: () => api.get(`/workorders/bike/${bikeId}/history`).then((r) => r.data),
  })
  const items = data?.history ?? []
  return (
    <div className="border-t border-gray-200 pt-3">
      <h4 className="font-semibold text-sm mb-2">🔧 История ремонтов</h4>
      {isLoading && <p className="text-xs text-gray-400">Загрузка…</p>}
      {!isLoading && items.length === 0 && <p className="text-xs text-gray-400">Ремонтов не было.</p>}
      <div className="space-y-2 max-h-52 overflow-auto">
        {items.map((w) => (
          <div key={w.id} className="text-xs bg-gray-50 rounded-lg p-2">
            <div className="flex justify-between gap-2">
              <span className="font-medium">{new Date(w.openedAt).toLocaleDateString('ru-RU')} · {w.reason}</span>
              <span className="text-gray-500 shrink-0">{w.mechanicName}</span>
            </div>
            {w.works.length > 0 && <div className="text-gray-600 mt-0.5">Работы: {w.works.map((x) => x.type + (x.hours != null ? ` (${x.hours}ч)` : '')).join(', ')}</div>}
            {w.parts.length > 0 && <div className="text-gray-600">Запчасти: {w.parts.map((p) => `${p.name}×${p.quantity}`).join(', ')}</div>}
            {w.totalCost != null && <div className="text-gray-500">Стоимость: {w.totalCost} ₽</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

async function generateBikeQrDataUrl(bike: Bike): Promise<string> {
  // Encode as a public URL so customer cameras open the rental info page
  const qrUrl = `${window.location.origin}/bike?qr=${encodeURIComponent(bike.qrData)}`
  const canvas = document.createElement('canvas')
  const SIZE = 300; const PADDING = 20; const TEXT_H = 60
  canvas.width = SIZE; canvas.height = SIZE + TEXT_H
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  const qrCanvas = document.createElement('canvas')
  await QRCode.toCanvas(qrCanvas, qrUrl, { width: SIZE - PADDING * 2, margin: 0 })
  ctx.drawImage(qrCanvas, PADDING, PADDING, SIZE - PADDING * 2, SIZE - PADDING * 2)
  ctx.fillStyle = '#111111'
  ctx.textAlign = 'center'
  ctx.font = 'bold 22px monospace'
  ctx.fillText(`#${bike.code}`, SIZE / 2, SIZE + 30)
  ctx.font = '16px sans-serif'
  ctx.fillStyle = '#555555'
  ctx.fillText(bike.model ?? '', SIZE / 2, SIZE + 52)
  return canvas.toDataURL('image/png')
}

async function downloadBikeQr(bike: Bike) {
  const dataUrl = await generateBikeQrDataUrl(bike)
  await downloadDataUrl(dataUrl, `bike-${bike.code}.png`)
}

/** Печать в новом окне только после полной сборки HTML (без пустого about:blank). */
function openQrPrintWindow(items: { code: string; dataUrl: string }[]) {
  const body = items
    .map(
      (i) =>
        `<div style="display:inline-block;margin:10px;text-align:center;border:1px solid #eee;padding:8px;border-radius:8px;page-break-inside:avoid"><img src="${i.dataUrl}" style="width:200px;height:240px;display:block" alt=""/><p style="font:bold 16px monospace;margin:8px 0 0">#${i.code}</p></div>`,
    )
    .join('')
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>QR Велосипеды</title></head><body style="font-family:sans-serif;padding:16px;text-align:center"><div style="margin-bottom:16px;font-weight:600">QR-коды велосипедов</div>${body}<script>window.addEventListener("load",function(){setTimeout(function(){window.print()},200)})</script></body></html>`
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const w = window.open(url, '_blank', 'noopener,noreferrer')
  if (!w) {
    URL.revokeObjectURL(url)
    alert('Браузер заблокировал всплывающее окно. Разрешите pop-up для этого сайта.')
    return
  }
  setTimeout(() => URL.revokeObjectURL(url), 120_000)
}

function splitCsvLine(line: string, sep: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++ }
      else { inQuotes = !inQuotes }
    } else if (ch === sep && !inQuotes) {
      result.push(current); current = ''
    } else { current += ch }
  }
  result.push(current)
  return result
}

function parseCsv(text: string): Record<string, string>[] {
  // Strip BOM and normalize line endings (handles Windows \r\n and old Mac \r)
  const clean = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const lines = clean.trim().split('\n').filter((l) => l.trim())
  if (lines.length < 2) return []
  // Auto-detect separator: semicolon (Russian Excel) vs comma
  const firstLine = lines[0]!
  const sep = firstLine.includes(';') && !firstLine.includes(',') ? ';' : ','
  const headers = splitCsvLine(firstLine, sep).map((h) => h.replace(/^"|"$/g, '').trim())
  return lines.slice(1).map((line) => {
    const values = splitCsvLine(line, sep)
    const obj: Record<string, string> = {}
    headers.forEach((h, i) => { obj[h] = (values[i] ?? '').replace(/^"|"$/g, '').trim() })
    return obj
  })
}

export default function BikesPage() {
  const qc = useQueryClient()
  const multipoint = useModule('multipoint')
  const serviceOn = useModule('service')
  const [searchParams, setSearchParams] = useSearchParams()
  const statusFilter = searchParams.get('status')
  const [showForm, setShowForm] = useState(false)
  const [editBike, setEditBike] = useState<Bike | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{ created: number; skipped: number; errors: string[] } | null>(null)
  const [exportingQr, setExportingQr] = useState(false)
  const [qrGallery, setQrGallery] = useState<{ code: string; dataUrl: string }[]>([])
  const csvImportRef = useRef<HTMLInputElement>(null)
  const [bulkMode, setBulkMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [transferPointId, setTransferPointId] = useState('')
  const [transferring, setTransferring] = useState(false)
  const [filterPointId, setFilterPointId] = useState('')
  const [bikeSearch, setBikeSearch] = useState('')

  const { data: bikesData } = useQuery({ queryKey: ['bikes'], queryFn: () => api.get('/bikes').then((r) => r.data) })
  const { data: pointsData } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const { data: tariffsData } = useQuery({ queryKey: ['tariffs'], queryFn: () => api.get('/tariffs').then((r) => r.data) })

  const createForm = useForm<CreateForm>()
  const editForm = useForm<EditForm>()

  const create = useMutation({
    mutationFn: (d: CreateForm) => api.post('/bikes', {
      code: d.code,
      tariffId: d.tariffId,
      model: d.model || undefined,
      pointId: d.pointId || undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bikes'] }); createForm.reset(); setShowForm(false) },
  })

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditForm }) =>
      api.patch(`/bikes/${id}`, {
        tariffId: data.tariffId || null,
        model: data.model || undefined,
        pointId: data.pointId || undefined,
        notes: data.notes || undefined,
        category: data.category?.trim() ? data.category.trim() : null,
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bikes'] }); setEditBike(null) },
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: string; notes?: string }) =>
      api.patch(`/bikes/${id}/status`, { status, notes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bikes'] }),
  })

  const deleteBike = useMutation({
    mutationFn: (id: string) => api.delete(`/bikes/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bikes'] }); setConfirmDeleteId(null) },
  })

  const importCsv = useMutation({
    mutationFn: (rows: { code: string; tariff: string; model?: string; pointName?: string; point?: string; notes?: string }[]) =>
      api.post('/exports/bikes/import', { rows }),
    onSuccess: (res) => { setImportResult(res.data.results); qc.invalidateQueries({ queryKey: ['bikes'] }) },
  })

  const openEdit = (b: Bike) => {
    editForm.reset({ tariffId: b.tariffId ?? '', model: b.model ?? '', pointId: b.point?.id ?? '', notes: b.notes ?? '', category: b.category ?? '' })
    setEditBike(b)
  }

  const toggleBulkMode = () => {
    setBulkMode((v) => !v)
    setSelectedIds(new Set())
    setTransferPointId('')
  }

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleBulkTransfer = async () => {
    if (!transferPointId || selectedIds.size === 0) return
    setTransferring(true)
    try {
      await Promise.all(
        [...selectedIds].map((id) => api.patch(`/bikes/${id}`, { pointId: transferPointId })),
      )
      qc.invalidateQueries({ queryKey: ['bikes'] })
      setBulkMode(false)
      setSelectedIds(new Set())
      setTransferPointId('')
    } finally {
      setTransferring(false)
    }
  }

  const handleCsvDownload = async () => {
    try {
      const res = await api.get('/exports/bikes.csv', { responseType: 'blob' })
      await downloadBlob(res.data as Blob, 'bikes.csv')
    } catch { alert('Ошибка экспорта') }
  }

  const handleCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return
    if (csvImportRef.current) csvImportRef.current.value = ''
    const text = await file.text()
    const rows = parseCsv(text).filter((r) => r.code)
    if (!rows.length) { alert('CSV не содержит корректных строк. Нужны колонки: code, tariff'); return }
    const missingTariff = rows.filter((r) => !r.tariff)
    if (missingTariff.length > 0) {
      const codes = missingTariff.slice(0, 5).map((r) => r.code).join(', ')
      const more = missingTariff.length > 5 ? ` и ещё ${missingTariff.length - 5}` : ''
      const ok = window.confirm(`⚠️ ${missingTariff.length} строк без тарифа (${codes}${more}) — они будут пропущены. Продолжить импорт остальных?`)
      if (!ok) return
    }
    importCsv.mutate(rows as { code: string; tariff: string; model?: string; pointName?: string; point?: string; notes?: string }[])
  }

  const handleExportAllQr = async () => {
    const bikes: Bike[] = bikesData?.bikes ?? []
    if (!bikes.length) return
    setExportingQr(true)
    try {
      const items: { code: string; dataUrl: string }[] = []
      for (const b of bikes) items.push({ code: b.code, dataUrl: await generateBikeQrDataUrl(b) })
      setQrGallery(items)
    } finally {
      setExportingQr(false)
    }
  }

  const normalizedSearch = bikeSearch.trim().toLowerCase()
  const visibleBikes: Bike[] = (bikesData?.bikes ?? [])
    .filter((b: Bike) => !statusFilter || b.status === statusFilter)
    .filter((b: Bike) => !filterPointId || b.point?.id === filterPointId)
    .filter((b: Bike) => {
      if (!normalizedSearch) return true
      const statusLabel = STATUS_LABELS[b.status]?.label ?? b.status
      return [
        b.code,
        b.model,
        b.point?.name,
        b.tariff?.name,
        statusLabel,
        b.notes,
      ].some((value) => value?.toLowerCase().includes(normalizedSearch))
    })

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Велосипеды ({visibleBikes.length}/{bikesData?.bikes?.length ?? 0})</h2>
        <button onClick={() => setShowForm(!showForm)} className="bg-brand-600 text-white px-4 py-2 rounded-xl text-sm font-medium">
          + Добавить
        </button>
      </div>


      <div className="relative mb-3">
        <input
          value={bikeSearch}
          onChange={(e) => setBikeSearch(e.target.value)}
          placeholder="Поиск: номер, модель, точка, тариф, статус"
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white pr-8"
        />
        {bikeSearch && (
          <button
            type="button"
            onClick={() => setBikeSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg leading-none"
          >
            ×
          </button>
        )}
      </div>

      {/* Filter by point */}
      {(pointsData?.points?.length ?? 0) > 1 && (
        <div className="flex gap-2 mb-3 flex-wrap">
          <button
            onClick={() => setFilterPointId('')}
            className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${!filterPointId ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600'}`}
          >
            Все точки
          </button>
          {pointsData.points.map((p: Point) => (
            <button key={p.id} onClick={() => setFilterPointId(p.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${filterPointId === p.id ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600'}`}>
              {p.name}
            </button>
          ))}
        </div>
      )}

      {/* Filter by status */}
      <div className="flex gap-2 mb-3 flex-wrap">
        {([
          ['', 'Все статусы'],
          ['free', 'Свободные'],
          ['in_use', 'В аренде'],
          ['reserved', 'Забронированы'],
          ['service', 'На обслуживании'],
        ] as const).map(([val, label]) => (
          <button
            key={val || 'all'}
            onClick={() => {
              if (val) searchParams.set('status', val)
              else searchParams.delete('status')
              setSearchParams(searchParams, { replace: true })
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${(statusFilter ?? '') === val ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={handleCsvDownload} className="text-xs border border-gray-300 text-gray-700 px-3 py-2 rounded-xl hover:bg-gray-50">
          📥 CSV выгрузка
        </button>
        <button onClick={() => csvImportRef.current?.click()} disabled={importCsv.isPending} className="text-xs border border-brand-300 text-brand-600 px-3 py-2 rounded-xl hover:bg-brand-50 disabled:opacity-60">
          📤 CSV импорт
        </button>
        <button onClick={handleExportAllQr} disabled={exportingQr} className="text-xs border border-purple-300 text-purple-700 px-3 py-2 rounded-xl hover:bg-purple-50 disabled:opacity-60">
          {exportingQr ? 'Генерация...' : '🖨️ Все QR (печать)'}
        </button>
        {multipoint && (
          <button onClick={toggleBulkMode}
            className={`text-xs px-3 py-2 rounded-xl border font-medium transition-colors ${bulkMode ? 'bg-amber-600 text-white border-amber-600' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}>
            {bulkMode ? '✕ Отменить выбор' : '🔀 Перенести велосипеды'}
          </button>
        )}
        <input ref={csvImportRef} type="file" accept=".csv" className="hidden" onChange={handleCsvImport} />
      </div>

      {/* Import result */}
      {importResult && (
        <div className={`border rounded-xl p-3 mb-4 ${importResult.errors.length > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
          <p className="text-sm font-semibold text-green-800">Импорт завершён: +{importResult.created} добавлено, {importResult.skipped} пропущено</p>
          {importResult.errors.length > 0 && (
            <div className="mt-2 space-y-1">
              {importResult.errors.map((e, i) => <p key={i} className="text-xs text-red-600">{e}</p>)}
            </div>
          )}
          <button onClick={() => setImportResult(null)} className="text-xs text-green-600 mt-2 underline">закрыть</button>
        </div>
      )}

      {/* CSV format hint */}
      <div className="text-xs text-gray-400 mb-4 bg-gray-50 rounded-xl p-3">
        Формат CSV для импорта: <code>code,tariff,model,pointName,notes</code><br />
        code — уникальный номер | <strong>tariff — точное название тарифа (обязательно)</strong> | model — модель (напр. Trek 820) | pointName — название точки как в системе<br />
        {tariffsData?.tariffs?.filter((t: Tariff & { isActive: boolean }) => t.isActive).length > 0 && (
          <span className="mt-1 block">
            Доступные тарифы: {tariffsData.tariffs.filter((t: Tariff & { isActive: boolean }) => t.isActive).map((t: Tariff) => <code key={t.id} className="bg-gray-200 rounded px-1 mr-1">{t.name}</code>)}
          </span>
        )}
        Поддерживаются разделители <code>,</code> и <code>;</code> (Excel), кодировки UTF-8 и BOM.
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4 space-y-3">
          <h3 className="font-semibold text-sm">Новый велосипед</h3>
          <form onSubmit={createForm.handleSubmit((d) => create.mutate(d))} className="space-y-3">
            <input {...createForm.register('code', { required: true })} placeholder="Код (напр. P1-001)" className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm" />
            <select {...createForm.register('tariffId', { required: true })} className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm">
              <option value="">Тариф (обязательно)</option>
              {tariffsData?.tariffs?.filter((t: Tariff & { isActive: boolean }) => t.isActive).map((t: Tariff) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <input {...createForm.register('model')} placeholder="Модель (напр. Trek 820)" className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm" />
            <select {...createForm.register('pointId')} className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm">
              <option value="">Точка (опционально)</option>
              {pointsData?.points?.map((p: Point) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="flex gap-2">
              <button type="submit" disabled={create.isPending} className="bg-brand-600 text-white px-4 py-2 rounded-xl text-sm disabled:opacity-60">
                {create.isPending ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="border border-gray-300 px-4 py-2 rounded-xl text-sm">Отмена</button>
            </div>
            {create.isError && (
              <p className="text-red-500 text-xs">{(create.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}</p>
            )}
          </form>
        </div>
      )}

      {/* Edit modal */}
      {editBike && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end" onClick={() => setEditBike(null)}>
          <div className="bg-white w-full rounded-t-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold">Редактировать #{editBike.code}</h3>
            <form onSubmit={editForm.handleSubmit((d) => update.mutate({ id: editBike.id, data: d }))} className="space-y-3">
              <select
                key={`tariff-${editBike.id}`}
                {...editForm.register('tariffId')}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm"
                defaultValue={editBike.tariffId ?? ''}
              >
                <option value="">— Без тарифа —</option>
                {tariffsData?.tariffs?.filter((t: Tariff & { isActive: boolean }) => t.isActive).map((t: Tariff) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <input {...editForm.register('model')} placeholder="Модель (напр. Trek 820)" className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm" />
              <select {...editForm.register('pointId')} className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm">
                <option value="">Без точки</option>
                {pointsData?.points?.map((p: Point) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <div>
                <input
                  {...editForm.register('category')}
                  list="bike-category-options"
                  placeholder="Категория (напр. Велосипед, Замок)"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm"
                />
                <datalist id="bike-category-options">
                  {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
                </datalist>
                <p className="text-xs text-gray-400 mt-1">Чтобы в отчётах считать велосипеды отдельно от замков и аксессуаров.</p>
              </div>
              <input {...editForm.register('notes')} placeholder="Заметка (опционально)" className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm" />
              <div className="flex gap-2">
                <button type="submit" disabled={update.isPending} className="flex-1 bg-brand-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
                  {update.isPending ? 'Сохранение...' : 'Сохранить'}
                </button>
                <button type="button" onClick={() => setEditBike(null)} className="flex-1 border border-gray-300 py-3 rounded-xl text-sm">Отмена</button>
              </div>
            </form>
            {serviceOn && <RepairHistory bikeId={editBike.id} />}
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDeleteId(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
            <p className="font-semibold text-center">Удалить велосипед?</p>
            <p className="text-sm text-gray-500 text-center">Нельзя удалить велосипед в активной аренде.</p>
            <div className="flex gap-3">
              <button onClick={() => deleteBike.mutate(confirmDeleteId)} disabled={deleteBike.isPending} className="flex-1 bg-red-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
                {deleteBike.isPending ? 'Удаление...' : 'Удалить'}
              </button>
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 border border-gray-300 py-3 rounded-xl text-sm">Отмена</button>
            </div>
            {deleteBike.isError && (
              <p className="text-red-500 text-xs text-center">{(deleteBike.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}</p>
            )}
          </div>
        </div>
      )}

      {/* QR Gallery — in-app (веб и Capacitor); печать — отдельное окно после сборки контента */}
      {qrGallery.length > 0 && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" onClick={() => setQrGallery([])}>
          <div className="flex items-center justify-between gap-2 p-4 text-white flex-wrap" onClick={(e) => e.stopPropagation()}>
            <p className="font-semibold">QR-коды ({qrGallery.length} шт.)</p>
            <div className="flex items-center gap-2">
              {!Capacitor.isNativePlatform() && (
                <button
                  type="button"
                  onClick={() => openQrPrintWindow(qrGallery)}
                  className="text-sm bg-white/10 hover:bg-white/20 px-3 py-2 rounded-xl border border-white/30"
                >
                  🖨️ Печать в новом окне
                </button>
              )}
              <button onClick={() => setQrGallery([])} className="text-white text-2xl leading-none px-2">×</button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="grid grid-cols-2 gap-4">
              {qrGallery.map((item) => (
                <div key={item.code} className="bg-white rounded-xl p-3 text-center">
                  <img src={item.dataUrl} alt={`#${item.code}`} className="w-full" />
                  <button
                    onClick={() => downloadDataUrl(item.dataUrl, `bike-${item.code}.png`)}
                    className="mt-2 text-xs text-brand-600 border border-brand-300 rounded-lg px-3 py-1"
                  >
                    Поделиться
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bikes list */}
      <div className={`space-y-2 ${bulkMode ? 'pb-32' : ''}`}>
        {visibleBikes.map((b: Bike) => {
          const st = STATUS_LABELS[b.status] ?? { label: b.status, color: 'bg-gray-100 text-gray-600' }
          const canSelect = bulkMode && b.status !== 'in_use'
          const isSelected = selectedIds.has(b.id)
          return (
            <div
              key={b.id}
              className={`bg-white border rounded-xl p-4 transition-colors ${isSelected ? 'border-amber-400 bg-amber-50' : 'border-gray-200'} ${canSelect ? 'cursor-pointer' : ''}`}
              onClick={canSelect ? () => toggleSelect(b.id) : undefined}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  {bulkMode && b.status !== 'in_use' && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(b.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-5 h-5 rounded accent-amber-600"
                    />
                  )}
                  <div>
                    <p className="font-medium">#{b.code}{b.model && <span className="text-gray-400 font-normal"> · {b.model}</span>}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                    <TariffDot color={b.tariff?.color} size={8} />
                    {b.tariff?.name ?? <span className="text-orange-400">Тариф не назначен</span>}
                  </p>
                    <p className="text-xs text-gray-400">
                      {b.point?.name ?? '—'}
                      {b.category && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{b.category}</span>}
                    </p>
                    {b.notes && <p className="text-xs text-gray-400 mt-0.5">{b.notes}</p>}
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${st.color}`}>{st.label}</span>
              </div>
              {!bulkMode && (
                <div className="flex items-center gap-2 flex-wrap">
                  {b.status !== 'in_use' && (
                    <select
                      defaultValue=""
                      onChange={(e) => e.target.value && setStatus.mutate({ id: b.id, status: e.target.value })}
                      className="text-xs border border-gray-300 rounded-lg py-1 px-2"
                    >
                      <option value="" disabled>Статус →</option>
                      {Object.entries(STATUS_LABELS).filter(([k]) => k !== b.status && k !== 'in_use').map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  )}
                  <button onClick={() => openEdit(b)} className="text-xs border border-gray-300 rounded-lg py-1 px-3 text-gray-600 hover:border-brand-400 hover:text-brand-600">
                    Изменить
                  </button>
                  <button onClick={() => downloadBikeQr(b)} className="text-xs border border-purple-300 rounded-lg py-1 px-3 text-purple-600 hover:bg-purple-50">
                    QR ↓
                  </button>
                  {b.status !== 'in_use' && (
                    <button onClick={() => setConfirmDeleteId(b.id)} className="text-xs border border-red-200 rounded-lg py-1 px-3 text-red-500 hover:bg-red-50">
                      Удалить
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Bulk transfer sticky bottom bar */}
      {bulkMode && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-amber-200 shadow-lg z-40 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-sm text-amber-800">
              {selectedIds.size > 0 ? `Выбрано: ${selectedIds.size} вел.` : 'Выберите велосипеды для переноса'}
            </p>
            <button onClick={toggleBulkMode} className="text-gray-400 text-xl leading-none">×</button>
          </div>
          <div className="flex gap-2">
            <select
              value={transferPointId}
              onChange={(e) => setTransferPointId(e.target.value)}
              className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm"
            >
              <option value="">Выберите точку назначения</option>
              {pointsData?.points?.map((p: Point) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <button
              onClick={handleBulkTransfer}
              disabled={transferring || selectedIds.size === 0 || !transferPointId}
              className="bg-amber-600 text-white px-5 py-2 rounded-xl text-sm font-semibold disabled:opacity-50"
            >
              {transferring ? '...' : 'Перенести →'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
